import type {
  AssignmentScore,
  GradebookAssignment,
  GradebookQuestionMeta,
  GradebookScoreKind,
  GradebookStudent,
  QuestionScore,
} from './gradebookTypes';

// A score counts as the maximum with this much give: a multi-part question's
// maximum is a sum of parts and can carry float noise.
const FULL_CREDIT_TOLERANCE = 1e-6;

export interface QuestionClassStats {
  // The mean score over every student; a student with no score counts as 0.
  average: number;
  // Students whose score is the question's maximum.
  fullCreditCount: number;
  studentCount: number;
  // Students with no attempt at it and no points on it.
  unansweredCount: number;
}

function findQuestionScore(
  assignmentScore: AssignmentScore | undefined,
  question: GradebookQuestionMeta,
): QuestionScore | undefined {
  const scores =
    question.kind === 'preLecture'
      ? assignmentScore?.preLectureQuestionScores
      : assignmentScore?.questionScores;
  return scores?.find(
    (score) =>
      score.index === question.index &&
      score.kind === question.kind &&
      score.sectionId === question.sectionId,
  );
}

/** How the class did on one question, from the loaded grades. */
export function questionClassStats(
  assignment: GradebookAssignment,
  question: GradebookQuestionMeta,
  students: GradebookStudent[],
): QuestionClassStats {
  let total = 0;
  let fullCreditCount = 0;
  let unansweredCount = 0;

  students.forEach((student) => {
    const score = findQuestionScore(student.assignmentScores[assignment.componentUid], question);
    const points = score?.score ?? 0;
    total += points;
    if (points >= question.maxScore - FULL_CREDIT_TOLERANCE) {
      fullCreditCount += 1;
    }
    if ((score?.attempts ?? 0) === 0 && points <= 0) {
      unansweredCount += 1;
    }
  });

  return {
    average: students.length > 0 ? total / students.length : 0,
    fullCreditCount,
    studentCount: students.length,
    unansweredCount,
  };
}

/** The class's mean score on one kind of an assignment's questions. */
export function assignmentClassAverage(
  assignment: GradebookAssignment,
  kind: GradebookScoreKind,
  students: GradebookStudent[],
): number {
  if (students.length === 0) {
    return 0;
  }
  const total = students.reduce((sum, student) => {
    const score = student.assignmentScores[assignment.componentUid];
    return sum + ((kind === 'preLecture' ? score?.preLectureScore : score?.homeworkScore) ?? 0);
  }, 0);
  return total / students.length;
}
